import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import type { TripList } from "@/types/scheduletrip";

type TripGroupProps = {
  title: string;
  trips: TripList[];
  selectedId: string;
  onSelect: (id: string) => void;
  loading?: boolean;
  skeletonCount?: number;
};

const formatTime = (time?: string) => {
  if (!time) return "-";

  const [hours, minutes] = time.split(":");

  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export function TripGroup({
  title,
  trips,
  selectedId,
  onSelect,
}: TripGroupProps) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-secondary-foreground">
        {title}
      </h2>
      {trips.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-slate-900/10 bg-card px-6 py-16 text-center">
          <p className="mt-2 text-sm text-muted-foreground">
            No trips available for this selection.
          </p>
        </div>
      ) : (
        <div
          className="mt-2 flex flex-col gap-3"
          role="radiogroup"
          aria-label={title}
        >
          {trips.map((trip) => {
            const isSelected = trip.operatorTripId === selectedId;
            const isFullyBooked = trip.seatStatus === "soldout";
            const isNotAvailable = trip.seatStatus === "notavailable";

            const isDisabled = isFullyBooked || isNotAvailable;

            return (
              <button
                key={trip.operatorTripId}
                type="button"
                onClick={() => onSelect(trip.operatorTripId)}
                disabled={isDisabled}
                role="radio"
                aria-checked={isSelected}
                className={cn(
                  "grid grid-cols-[24px_1fr_112px] w-full items-center gap-4 rounded-2xl border-2 px-4 py-6 text-left transition-colors shadow-md",
                  isDisabled
                    ? "cursor-not-allowed border-transparent bg-card/60"
                    : "bg-card hover:border-brand/60",
                  isSelected ? "border-primary border-2" : "border-transparent",
                )}
              >
                <span
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full border",
                    isSelected
                      ? "border-primary text-primary"
                      : "border-primary",
                    isDisabled && "border-border/60",
                  )}
                >
                  {isSelected ? <Check size={16} strokeWidth={3} /> : null}
                </span>
                <span
                  className={cn(
                    "flex-1 text-center text-lg font-semibold md:text-left",
                    isDisabled
                      ? "text-secondary-foreground/60"
                      : "text-secondary-foreground",
                  )}
                >
                  {formatTime(trip.departTime)}
                </span>
                <span
                  className={cn(
                    "shrink-0 text-sm font-medium mr-4 text-end",
                    isDisabled
                      ? "text-destructive/60"
                      : isSelected
                        ? "text-primary"
                        : "text-muted",
                  )}
                >
                  {isFullyBooked
                    ? "Fully Booked"
                    : isNotAvailable
                      ? "Not Available"
                      : isSelected
                        ? "Selected"
                        : "Available"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
